import React, { type PropsWithChildren, useCallback, useMemo } from 'react';

import UFOSegment from '@atlaskit/react-ufo/segment';
import { getFrontendExperiment } from '@atlassian/search-experiment/frontend';
import { useExperimentConfig } from '@atlassian/search-experiment/statsig';

import type { BootstrapState, Features } from '../../../../common/constants/bootstrap';
import { type NounKeys } from '../../../../common/constants/nouns';
import {
	getAllProductKeys,
	type PrimaryProductKey,
	ProductKeys,
} from '../../../../common/constants/product-keys';
import { type SearchQueryStringParamsRaw } from '../../../../common/constants/schemas/query-params';
import type { SearchStoreExperience } from '../../../../common/constants/search';
import { SearchExperience } from '../../../../common/constants/search-experience';
import { type UserDetails } from '../../../../common/constants/types';
import { getMultiSiteSearchInRovo } from '../../../../common/utils/experiments';
import { getAccessibleProducts } from '../../../../common/utils/get-accessible-products';
import { useFetchSmartLinkUsage } from '../../../3p-smart-link-usage';
import { useFetchSmartLinkUsageAndTopApps } from '../../../3p-smart-link-usage-and-top-apps';
import { useFetchConfig, type UseFetchConfigProps } from '../../../config';
import { useLogException } from '../../../error-boundary/utils';
import { BootstrapContextProvider } from '../bootstrap-context-provider';
import { BootstrapStoreContainer } from '../index';
import { serveBootstrapProviderExp } from '../utils';

export type BootstrapContainerProps = PropsWithChildren<
	{
		/**
		 * Passing this will override the behaviour of bootstrapping in all product mode.
		 * The UI will only show the products which have been passed PROVIDED the user has access to them.
		 *
		 * NOTE: QS-4325: The `products` parameter is deprecated as we are removing it as a
		 * prop from the root <SearchPage/>.
		 */
		products?: ProductKeys[];

		nouns?: NounKeys[];

		/**
		 * The product the search page will be rendered in.
		 */
		primaryProduct: PrimaryProductKey;

		/**
		 * The sub-product within the primary product the search page will be rendered in.
		 */
		primarySubProduct?: string;

		/**
		 * This restricts the searches only to the cloudId that was passedin.
		 * This is a Jira/Confluence term and unfortunately most of the Atlassian products continue to be tied to a site.
		 *
		 * This propertly is useless for Trello or Bitbucket which are Workspace scoped.
		 *
		 * Not passing this property would bootstrap the UI in the site on which the user is most active
		 */
		cloudId?: string;

		/**
		 * The absolute url to the Atlassian Graphql Gateway. If not passed in the relative URL would be used.
		 */
		aggAbsoluteUrl?: string;

		/**
		 * Details of the current user.
		 */
		user?: UserDetails;

		/**
		 * Extra attributes to be attached to analytics events that are fired from within Full Page Search
		 */
		analyticsAttributes?: Record<string, any>;

		SAINHostName?: string;

		orgId?: string;

		queryParams?: Record<string, any>;

		/**
		 * Features that are enabled/disabled for the search page
		 */
		features?: Features;

		/**
		 * A unique identifier for the search session. If not passed, a random UUID will be generated.
		 */
		searchSessionId?: string | null;

		callbacks?: {
			/**
			 * A callback that is invoked when the query parameters in the URL need to be updated.
			 * The consuming product can implement the specific steps to be done when this callback is invoked.
			 *
			 * @param queryParamObject An object which is key:value map of things that should be set as the query parameter.
			 */
			updateQueryParameters?: (queryParamObject: Partial<SearchQueryStringParamsRaw>) => void;

			/**
			 * @deprecated this prop is deprecated and not used anymore. It will be removed soon.
			 *
			 * @returns the space IDs for the given space keys. NOTE: these should be the space IDs, not ARIs.
			 * @example getSpaceIdsFromSpaceKeys(["STATUS"]); // Promise<["123454321"]>
			 **/
			getSpaceIdsFromSpaceKeys?: (spaceKeys: string[]) => Promise<string[]>;

			/**
			 * A callback that is invoked when the Quick Find search completes successfully.
			 */
			onSearchCompleted?: (resultCount?: number, query?: string) => void;
		};

		/**
		 * The Statsig layer used to run mutually exclusive frontend and backend experiments
		 */
		statsigLayer?: string;
		/**
		 * Each product is responsible for providing the list of backend search experiment layers.
		 * Each string in the list is a stringified JSON object that represents a layer and
		 * the set of criteria for a search request to match on and be enrolled in a given backend experiment.
		 * This is defined via Statsig dynamic config: https://console.statsig.com/LqivKg6ADZZaGczRfBKfX/dynamic_configs/search_platform_layer_functions
		 */
		experimentLayers?: string[];

		/**
		 * The search experience for which the bootstrap store is being provided. This is important to pass in as a prop as it may be utilised e.g. in useFetchConfig before the stores are initialised.
		 *
		 * Optional because from https://bitbucket.org/atlassian/atlassian-frontend-monorepo/pull-requests/226846/overview this is now exported for Rovo Home, we'll need to upgrade that before making this prop required. Defaults to SearchExperience.FULL_PAGE_SEARCH
		 */
		experience?: SearchStoreExperience;

		/**
		 * This determines if board entities can be fetched or not
		 */
		hasEditOrViewPlanPermission?: boolean;
	} & Pick<
		UseFetchConfigProps,
		'configFetchAbsoluteUrl' | 'aggregatorHostName' | 'preloadDataProvider'
	> & {
			/**
			 * @deprecated This prop is no-op and will be removed in a future major release.
			 */
			thirdPartyAbsoluteUrl?: string;
			/**
			 * @deprecated This prop is no-op and will be removed in a future major release.
			 */
			thirdPartyDisabled?: boolean;
		}
>;

export const BootstrapStoreProvider = ({
	children,
	experience = SearchExperience.SEARCH_PAGE,
	...props
}: PropsWithChildren<BootstrapContainerProps>): React.JSX.Element => {
	const {
		cloudId,
		configFetchAbsoluteUrl,
		user,
		primaryProduct,
		primarySubProduct,
		aggAbsoluteUrl,
		SAINHostName,
		aggregatorHostName,
		preloadDataProvider,
		orgId,
		analyticsAttributes,
		products,
		queryParams,
		features,
		searchSessionId,
		callbacks,
		experimentLayers,
		hasEditOrViewPlanPermission,
	} = props;

	const onError = useLogException();
	const bootstrapProviderExp = serveBootstrapProviderExp();
	const smartLinkUsage = useFetchSmartLinkUsage(experience, cloudId);

	// Under rovo-search-remove-rss-bootstrap-fg, experimentConfig is computed in StaticContextProvider
	// via getExperimentConfig. We still call useExperimentConfig unconditionally (rules of hooks) but don't
	// pass the result through initialProps on the FG-on path.

	// Keep in sync with: bootstrap-context-provider/static-context-provider/index.tsx
	const experimentConfig = useExperimentConfig({
		experimentLayers: experimentLayers ?? [],
		sideBySideExperiment: features?.sideBySide?.experiments,
		statsigLayer: undefined,
		onError,
	});

	// Compute multiSiteSearchInRovo independently for useFetchConfig.
	// Under FG-on, we avoid depending on experimentConfig for this value since experimentConfig
	// will be owned by StaticContextProvider. This direct Statsig call is pure and side-effect-free.
	const multiSiteSearchInRovo = serveBootstrapProviderExp()
		? getFrontendExperiment({ abTestId: 'multi_site_search_in_rovo', onError }).cohort === 'test'
		: getMultiSiteSearchInRovo(experimentConfig);

	const {
		accessibleProducts,
		accessibleNouns,
		thirdPartyConfigs,
		firstCloudId,
		intercomHmac,
		isRovoEnabled,
		isJiraIssueIndexed,
		productEdition,
		productDataRegions,
		rovoFeatures,
		isConfigLoaded,
		linkedCloudARIs,
		unitMetadata,
		onOAuthSuccess,
	} = useFetchConfig({
		cloudId,
		configFetchAbsoluteUrl,
		aggregatorHostName,
		preloadDataProvider,
		userId: user?.id,
		primaryProduct,
		aggAbsoluteUrl,
		multiSiteSearchInRovo,
		experience,
		activationId: analyticsAttributes?.activationId,
	});

	// Must be called after useFetchConfig so we have the resolved thirdPartyConfigs
	// and productEdition. Reading these from the bootstrap store here would be a
	// circular read.
	const smartLinkUsageAndTopApps = useFetchSmartLinkUsageAndTopApps(
		experience,
		primaryProduct,
		cloudId,
		thirdPartyConfigs,
		productEdition,
	);

	const allFilterExcludedProducts = useMemo(
		() => [ProductKeys.Trello, ProductKeys.Gmail, ProductKeys.OutlookMail],
		[],
	);

	const getInitialProps = useCallback(() => {
		let storeContainerInitialProps: Partial<BootstrapState> = {
			cloudId: cloudId ?? firstCloudId,
			aggAbsoluteUrl,
			SAINHostName,
			aggregatorHostName,
			user,
			primaryProduct,
			primarySubProduct,
			orgId,
			analyticsAttributes,
			features,
			// Under rovo-search-remove-rss-bootstrap-fg, experimentConfig is computed
			// in StaticContextProvider via getExperimentConfig instead of being passed through here.
			...(!serveBootstrapProviderExp() && { experimentConfig }),
			experimentLayers,
			intercomHmac,
			isRovoEnabled,
			isJiraIssueIndexed,
			isConfigLoaded,
			// Under rovo-search-remove-rss-bootstrap-fg, getAccessibleProducts is computed
			// in StaticContextProvider instead of here. Pass raw products through.
			products: serveBootstrapProviderExp()
				? products
				: getAccessibleProducts({
						supportedProducts: getAllProductKeys(),
						products,
						accessibleProducts,
					}),
			nouns: accessibleNouns,
			allFilterExcludedProducts,
			productEdition,
			productDataRegions,
			rovoFeatures,
			linkedCloudARIs,
			unitMetadata,
			hasEditOrViewPlanPermission,
			smartLinkUsage,
			smartLinkUsageAndTopApps,
		};

		if (searchSessionId) {
			storeContainerInitialProps.searchSessionId = searchSessionId;
		}
		if (accessibleProducts) {
			storeContainerInitialProps.thirdPartyConfigs = thirdPartyConfigs;
		}

		return storeContainerInitialProps;
	}, [
		cloudId,
		firstCloudId,
		aggAbsoluteUrl,
		SAINHostName,
		aggregatorHostName,
		user,
		primaryProduct,
		primarySubProduct,
		orgId,
		analyticsAttributes,
		features,
		experimentConfig,
		intercomHmac,
		isRovoEnabled,
		isJiraIssueIndexed,
		isConfigLoaded,
		products,
		accessibleProducts,
		accessibleNouns,
		allFilterExcludedProducts,
		productEdition,
		productDataRegions,
		searchSessionId,
		thirdPartyConfigs,
		rovoFeatures,
		linkedCloudARIs,
		unitMetadata,
		experimentLayers,
		hasEditOrViewPlanPermission,
		smartLinkUsage,
		smartLinkUsageAndTopApps,
	]);

	if (bootstrapProviderExp) {
		const initialProps = getInitialProps();
		return (
			<UFOSegment name="bootstrap-context">
				<BootstrapContextProvider
					initialProps={initialProps}
					queryParams={queryParams}
					queryParamsCallback={callbacks?.updateQueryParameters}
					getSpaceIdsFromSpaceKeys={callbacks?.getSpaceIdsFromSpaceKeys}
					onSearchCompleted={callbacks?.onSearchCompleted}
					onOAuthSuccess={onOAuthSuccess}
					experience={experience}
					accessibleProducts={accessibleProducts}
				>
					{children}
				</BootstrapContextProvider>
			</UFOSegment>
		);
	} else {
		return (
			<UFOSegment name="bootstrap-rss">
				<BootstrapStoreContainer
					initialProps={getInitialProps()}
					queryParams={queryParams}
					queryParamsCallback={callbacks?.updateQueryParameters}
					getSpaceIdsFromSpaceKeys={callbacks?.getSpaceIdsFromSpaceKeys}
					onSearchCompleted={callbacks?.onSearchCompleted}
					onOAuthSuccess={onOAuthSuccess}
				>
					{children}
				</BootstrapStoreContainer>
			</UFOSegment>
		);
	}
};
